import { AppService } from '@services/app/app.service';
import { Injectable } from '@angular/core';
import {
  AnalyticsCtaClickEventModel,
  AnalyticsErrorEventInputModel,
  AnalyticsErrorEventModel,
  AnalyticsEventDataModel,
  AnalyticsEventModel,
  AnalyticsEventName,
  AnalyticsInputFocusEventModel,
  AnalyticsOutcomeEventModel,
  AnalyticsPageviewEventModel,
  AnalyticsPopupEventModel
} from '@core/models/analytics';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private eventData$ = new Subject<AnalyticsEventDataModel>();

  constructor(private appService: AppService) {}

  getEventDataStream(): Observable<AnalyticsEventDataModel> {
    return this.eventData$.asObservable();
  }

  pushPageViewEvent(): void {
    if (!this.isAnalyticsEventEnabled('virtual_pageview')) {
      return;
    }

    const event: AnalyticsPageviewEventModel = {
      ...this.getBaseEvent('virtual_pageview'),
      page: {
        path: '',
        title: ''
      },
      http: {
        response: {
          code: '200'
        }
      }
    };

    this.pushEvent(event);
  }

  pushPopupEvent(): void {
    if (!this.isAnalyticsEventEnabled('virtual_pageview')) {
      return;
    }

    const event: AnalyticsPopupEventModel = {
      ...this.getBaseEvent('virtual_pageview'),
      type: '',
      popup: {
        action: '',
        elements: {
          title: '',
          content: '',
          name: '',
          cta: {
            existing: [],
            clicked: ''
          }
        }
      }
    };

    this.pushEvent(event);
  }

  pushCtaClickEvent(): void {
    if (!this.isAnalyticsEventEnabled('cta_click')) {
      return;
    }

    const event: AnalyticsCtaClickEventModel = {
      ...this.getBaseEvent('cta_click'),
      cta: {
        text: '',
        class: '',
        ['target_url']: ''
      }
    };

    this.pushEvent(event);
  }

  pushInputFocusEvent(): void {
    if (!this.isAnalyticsEventEnabled('input_field_focus')) {
      return;
    }

    const event: AnalyticsInputFocusEventModel = {
      ...this.getBaseEvent('input_field_focus'),
      ['input_field']: {
        label: '',
        id: '',
        class: ''
      }
    };

    this.pushEvent(event);
  }

  pushErrorEvent(input: AnalyticsErrorEventInputModel): void {
    if (!this.isAnalyticsEventEnabled('custom_error')) {
      return;
    }

    const event: AnalyticsErrorEventModel = {
      ...this.getBaseEvent('custom_error'),
      error: {
        ['error_type']: input.error_type,
        ['error_code']: input.error_code,
        ['error_message']: input.error_message,
        ['error_field']: input.error_field,
        ['is_fatal']: input.is_fatal
      }
    };

    this.pushEvent(event);
  }

  pushOutcomeEvent(): void {
    if (!this.isAnalyticsEventEnabled('request_outcome')) {
      return;
    }

    const event: AnalyticsOutcomeEventModel = {
      ...this.getBaseEvent('request_outcome'),
      request: {
        id: '',
        description: ''
      },
      response: {
        code: 0,
        status: 0,
        message: ''
      }
    };

    this.pushEvent(event);
  }

  isAnalyticsEventEnabled(name: AnalyticsEventName): boolean {
    const offList = this.appService.getConfig().analytics.eventsOff || [];

    return this.isAnalyticsEnabled() && !offList.includes(name);
  }

  isAnalyticsEnabled(): boolean {
    return this.appService.getConfig().analytics.enabled === true;
  }

  private getBaseEvent(name: AnalyticsEventName): AnalyticsEventModel {
    return {
      language: navigator.language,
      ['procedure_id']: '',
      // card: {
      //   scheme: '',
      //   abi: '',
      //   affinity: '',
      //   cpp: ''
      // },
      ['campaign_id']: '',
      event: name
    };
  }

  private pushEvent(event: AnalyticsEventModel): void {
    this.eventData$.next({ name: event.event, event });
  }
}
